import {
  conversationRecordSchema,
  agentDirectorySchema,
  type AgentDirectory,
  type CreateStandaloneConversation,
  type PromoteConversation,
  type ConversationRunPayer,
  type ConversationRecord,
  type ConversationSidebarUpdate,
} from "@opencloud/agent-protocol";
import { api, browserRequestId } from "./api.js";
import type { AgentAppView, ConversationSnapshot, ConversationThread, ConversationMessage, ConversationRun, OperationView } from "./types.js";

export type StandaloneConversationInput = CreateStandaloneConversation;

export type StandaloneSnapshot = Omit<ConversationSnapshot, "app" | "thread" | "messages" | "runs"> & {
  convertedApp?: AgentAppView;
  app: null;
  thread: Omit<ConversationThread, "appId"> & { appId: null };
  messages: (Omit<ConversationMessage, "appId"> & { appId: null })[];
  runs: (ConversationRun & { payer: ConversationRunPayer | null })[];
  conversation: ConversationRecord;
};

export async function readAgentDirectory(signal?: AbortSignal): Promise<AgentDirectory> {
  return agentDirectorySchema.parse(await api("/v1/directory", { ...(signal ? { signal } : {}) }));
}

export async function listConversations(
  signal?: AbortSignal,
  options: { archived?: boolean } = {},
): Promise<ConversationRecord[]> {
  const result = await api<{ schemaVersion: 1; conversations: unknown[] }>(
    options.archived ? "/v1/conversations?archived=true" : "/v1/conversations",
    { ...(signal ? { signal } : {}) },
  );
  return result.conversations.map((value) => conversationRecordSchema.parse(value));
}

export async function createStandaloneConversation(
  input: StandaloneConversationInput,
): Promise<ConversationRecord> {
  const result = await api<{ schemaVersion: 1; conversation: unknown }>(
    "/v1/conversations",
    { method: "POST", body: JSON.stringify(input) },
  );
  return conversationRecordSchema.parse(result.conversation);
}

export async function updateConversationSidebar(
  conversationId: string,
  input: ConversationSidebarUpdate,
): Promise<{ conversation: ConversationRecord; archived: boolean }> {
  const result = await api<{ schemaVersion: 1; conversation: unknown; archived: boolean }>(
    conversationPath(conversationId),
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return {
    conversation: conversationRecordSchema.parse(result.conversation),
    archived: result.archived,
  };
}

export async function readStandaloneConversation(
  conversationId: string,
  signal?: AbortSignal,
): Promise<StandaloneSnapshot> {
  const snapshot = await api<StandaloneSnapshot>(conversationPath(conversationId), {
    ...(signal ? { signal } : {}),
  });
  return { ...snapshot, conversation: conversationRecordSchema.parse(snapshot.conversation) };
}

/** The caller retains this request across ambiguous outcomes. Never allocate it in retry. */
export function standaloneMessageIntent(content: string, attachmentIds?: string[]) {
  return { requestId: browserRequestId(), sentAt: new Date().toISOString(), content,
    ...(attachmentIds?.length ? {attachmentIds} : {}) };
}

export async function sendStandaloneMessage(
  conversationId: string,
  intent: ReturnType<typeof standaloneMessageIntent>,
): Promise<{ schemaVersion: 1; runId: string; messageId: string; status: "queued" }> {
  return api(`${conversationPath(conversationId)}/messages`, {
    method: "POST",
    body: JSON.stringify(intent),
  });
}

export async function cancelStandaloneRun(
  conversationId: string,
  runId: string,
  requestId: string,
): Promise<OperationView> {
  return api<OperationView>(
    `${conversationPath(conversationId)}/runs/${encodeURIComponent(runId)}/cancel`,
    { method: "POST", body: JSON.stringify({ requestId, sentAt: new Date().toISOString() }) },
  );
}

export async function promoteStandaloneConversation(
  conversationId: string,
  input: PromoteConversation,
): Promise<{ app: AgentAppView; conversation: ConversationRecord }> {
  const result = await api<{
    schemaVersion: 1;
    app: AgentAppView;
    conversation: unknown;
  }>(`${conversationPath(conversationId)}/create-app`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return { app: result.app, conversation: conversationRecordSchema.parse(result.conversation) };
}

function conversationPath(id: string): string {
  return `/v1/conversations/${encodeURIComponent(id)}`;
}
